import { sentenceCase } from 'change-case'
import { Eye, Pencil, Snowflake, Sun, X } from 'lucide-react'
import React, { useState } from 'react'

import { isPrivilegedRole } from '@ankor-io/common/domain/iam/account'
import { AWXCardStatus } from '@ankor-io/common/types/awx'
import type { CardPayload } from '@ankor-io/common/types/cards'

import { useUserContext } from '@/providers/user/useUserContext'
import { Badge } from '@/ui-components/Badge'
import { Button } from '@/ui-components/Button'
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@/ui-components/Dropdown'
import { Heading } from '@/ui-components/Heading'
import CancelCardModal from '@/views/components/CancelCardModal'
import FreezeCardModal from '@/views/components/FreezeCardModal'
import { UnfreezeCardModal } from '@/views/components/UnfreezeCardModal'
import { VirtualCard } from '@/views/components/VirtualCard'

type MainCardDetailsProps = {
  cardDetails: CardPayload
  onCardUpdate: (updatedCard: CardPayload) => void
}

type FreezeUnfreezeDropdownItemProps = {
  card: CardPayload
  onUnfreeze: () => void
  onFreeze: () => void
}

const FreezeUnfreezeDropdownItem = ({ card, onUnfreeze, onFreeze }: FreezeUnfreezeDropdownItemProps) => {
  const isInactive = card.status === AWXCardStatus.INACTIVE

  return (
    <DropdownItem
      onClick={() => (isInactive ? onUnfreeze() : onFreeze())}
      data-card-status={card.status}
      data-action-type="freeze-unfreeze-card"
    >
      {isInactive ? <Sun data-slot="icon" /> : <Snowflake data-slot="icon" />}
      {isInactive ? 'Unfreeze' : 'Freeze'}
    </DropdownItem>
  )
}

const MainCardDetails: React.FC<MainCardDetailsProps> = ({ cardDetails, onCardUpdate }) => {
  const { activeOrganizationId, activeOrganizationRole } = useUserContext()

  const [isFlipped, setIsFlipped] = React.useState(false)

  const [isCancelCardModalOpen, setIsCancelCardModalOpen] = useState(false)
  const [isFreezeCardModalOpen, setIsFreezeCardModalOpen] = useState(false)
  const [isUnfreezeCardModalOpen, setIsUnfreezeCardModalOpen] = useState(false)

  return (
    <>
      <div className="xl:w-[25rem] shrink-0 border rounded-lg shadow-sm dark:shadow-gray-800 border-gray-200 dark:border-gray-700">
        <div className="flex gap-2 items-center justify-between p-4 md:px-8 xl:p-4">
          <Heading level={3}>Card ending in: {cardDetails.maskedCardNumber.slice(-4)}</Heading>
          {cardDetails.status !== AWXCardStatus.CLOSED && (
            <Dropdown>
              <DropdownButton color="dark">Actions</DropdownButton>
              <DropdownMenu anchor="bottom end">
                {/* Edit Card */}
                <DropdownItem
                  to={`/cards/card-edit/${cardDetails.id}`}
                  data-card-status={cardDetails.status}
                  data-action-type="edit-card"
                >
                  <Pencil data-slot="icon" />
                  Edit
                </DropdownItem>

                {/* Freeze/Unfreeze Card */}
                <FreezeUnfreezeDropdownItem
                  card={cardDetails}
                  onUnfreeze={() => setIsUnfreezeCardModalOpen(true)}
                  onFreeze={() => setIsFreezeCardModalOpen(true)}
                />

                {/* Cancel Card */}
                <DropdownItem
                  onClick={() => setIsCancelCardModalOpen(true)}
                  data-card-status={cardDetails.status}
                  data-action-type="cancel-card"
                >
                  <X data-slot="icon" />
                  Cancel
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          )}
        </div>
        <hr className="border-gray-200 dark:border-gray-700" />
        <div className="p-4 md:p-8 xl:p-4">
          <div className="flex flex-col items-center gap-y-4 mb-8">
            <div className="relative">
              <VirtualCard
                cardId={cardDetails.id}
                cardHolder={`${cardDetails.cardholder.firstName} ${cardDetails.cardholder.lastName}`}
                cardLast4Digits={cardDetails.maskedCardNumber.slice(-4)}
                isFlipped={isFlipped}
              />
            </div>

            {activeOrganizationId && (
              <Button
                color="primary"
                onClick={() => setIsFlipped(!isFlipped)}
                disabled={!isPrivilegedRole(activeOrganizationRole)}
              >
                {/* Use the eye icon from lucide */}
                <Eye data-slot="icon" />
                <span>{isFlipped ? 'Hide card details' : 'View card details'}</span>
              </Button>
            )}
          </div>
          {/* Card details section */}
          <div className="flex flex-col gap-0">
            {/* Card Nickname */}
            {cardDetails.nickName && (
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Card nickname</div>
                <div className="font-semibold text-gray-900 dark:text-gray-50">{cardDetails.nickName}</div>
              </div>
            )}
            <hr className="my-5 border-gray-200 dark:border-gray-700" />

            {/* Card Status */}
            <div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Card status</div>
              <Badge color="green">{sentenceCase(cardDetails.status)}</Badge>
            </div>
            <hr className="my-5 border-gray-200 dark:border-gray-700" />
          </div>
        </div>
      </div>

      <CancelCardModal
        onClose={() => setIsCancelCardModalOpen(false)}
        isOpen={isCancelCardModalOpen}
        cardId={cardDetails.id}
        onSuccess={(updatedCard) => {
          onCardUpdate({ ...cardDetails, status: updatedCard.status })
        }}
      />
      <FreezeCardModal
        onClose={() => setIsFreezeCardModalOpen(false)}
        isOpen={isFreezeCardModalOpen}
        cardId={cardDetails.id}
        onSuccess={(updatedCard) => {
          onCardUpdate({ ...cardDetails, status: updatedCard.status })
        }}
      />
      <UnfreezeCardModal
        onClose={() => setIsUnfreezeCardModalOpen(false)}
        isOpen={isUnfreezeCardModalOpen}
        cardId={cardDetails.id}
        onSuccess={(updatedCard) => {
          onCardUpdate({ ...cardDetails, status: updatedCard.status })
        }}
      />
    </>
  )
}

export default MainCardDetails
